import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { ActivityLogsContext } from '../../../shared/context/logsContext';

interface Column {
    id?: number;
    propertyName: 'id' | 'userName' | 'userEmail' | 'description' | 'toDate' | 'url' | 'city' | 'country' | 'deviceType' | 'browser' | 'operatingSystem';
    label: string;
    minWidth?: number;
    align?: 'center';
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    {
        propertyName: 'id',
        label: 'Index',
        minWidth: 60,
        align: 'center'
    },
    {
        propertyName: 'userName',
        label: 'Name',
        minWidth: 170,
        align: 'center'
    },
    {
        propertyName: 'userEmail',
        label: 'Email',
        minWidth: 170,
        align: 'center'
    },
    {
        propertyName: 'description',
        label: 'Description',
        minWidth: 480,
        align: 'center',
        format: (value: number) => value.toLocaleString('en-US'),
    },
    {
        propertyName: 'toDate',
        label: 'Created date',
        minWidth: 170,
        align: 'center',
        format: (value: number) => value.toLocaleString('en-US'),
    },
    {
        propertyName: 'url',
        label: 'URL',
        minWidth: 170,
        align: 'center',
        format: (value: number) => value.toFixed(2),
    },
    {
        propertyName: 'city',
        label: 'City',
        minWidth: 170,
        align: 'center',
        format: (value: number) => value.toFixed(2),
    },
    {
        propertyName: 'country',
        label: 'Country',
        minWidth: 170,
        align: 'center',
        format: (value: number) => value.toFixed(2),
    },
    {
        propertyName: 'deviceType',
        label: 'Device',
        minWidth: 170,
        align: 'center',
        format: (value: number) => value.toFixed(2),
    },
    {
        propertyName: 'browser',
        label: 'Browser',
        minWidth: 170,
        align: 'center',
        format: (value: number) => value.toFixed(2),
    },
    {
        propertyName: 'operatingSystem',
        label: 'OS',
        minWidth: 170,
        align: 'center',
        format: (value: number) => value.toFixed(2),
    },
];

interface Data {
    id: number;
    userName: string;
    userEmail: string;
    description: string;
    toDate: string;
    url: string;
    city: string;
    country: string;
    deviceType: string;
    browser: string;
    operatingSystem: string;
}


export default function ActivityLogs() {
    const logs = React.useContext(ActivityLogsContext as any) as any[];
    const rows = JSON.parse(sessionStorage.getItem('activityLogs') as string)?.map((el: any, i: number) => ({ ...el, id: i + 1 })) as Data[] ?? logs?.map((el: any, i: number) => ({ ...el, id: i + 1 }));
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    return (
        <Box component={'section'} p={3} bgcolor={"#f5f5f5"} height={"100%"}>
            {/* <Box sx={{ width: '100%', bgcolor: "#fff", borderRadius: 2, boxShadow: 2, p: 3 }}> */}
                <Typography variant="h5" color="primary" mb={2} fontWeight={600}>
                    Activity logs
                </Typography>
                 {/* height 636px */}
                <Paper sx={{ width: '100%', overflow: 'hidden', border: 1, borderColor: 'rgb(224, 224, 224)' }}>
                    <TableContainer sx={{ height: 630 }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow sx={{'& .MuiTableCell-head.MuiTableCell-stickyHeader': {
                                    backgroundColor: '#ebedf3'
                                }}}>
                                    {columns.map((column, i) => (
                                        <TableCell
                                            sx={{ borderRight: '1px solid #ccc', fontWeight: 600 }}
                                            key={i}
                                            align={column.align}
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows?.length && rows
                                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row) => {
                                        return (
                                            <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                                {columns.map((column, i) => {
                                                    const value = row[column.propertyName];
                                                    return (
                                                        <TableCell sx={{ borderRight: '1px solid #ccc' }} key={i} align={column.align}>
                                                            {column.propertyName === 'url' ?
                                                                (<NavLink to={value as string}>
                                                                    {column.format && typeof value === 'number'
                                                                        ? column.format(value)
                                                                        : value}
                                                                </NavLink>) : (<span dangerouslySetInnerHTML={{
                                                                    __html: (column.format && typeof value === 'number'
                                                                        ? column.format(value)
                                                                        : value as any)
                                                                }}></span>)
                                                            }
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                        {!rows?.length && <Box component={'div'} height={"80%"} width={"100%"} display={'flex'} textAlign={'center'} justifyContent={'center'} alignItems={'center'}>
                            No data found
                        </Box>}
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={rows?.length ?? 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            {/* </Box> */}
        </Box>
    );
}
